import { useState, useRef, useCallback } from 'react';
import debounce from 'lodash.debounce';

export const useSearch = (apiEndpoint, type) => {
  const [searchResults, setSearchResults] = useState([]);
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [proteinLookupStatus, setProteinLookupStatus] = useState(null);
  
  // Add state for tab data, loading and errors
  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [tabErrors, setTabErrors] = useState({});
  
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  });
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  // Memoize the debounced autocomplete function
  const fetchAutocompleteSuggestions = useCallback(
    debounce(async (term) => {
      if (!term || term.length < 2) {
        setAutocompleteResults([]);
        setShowAutocomplete(false);
        return;
      }

      try {
        const response = await fetch(
          `/api/autocomplete?q=${encodeURIComponent(term)}&type=${type}`
        );
        const data = await response.json();
        let results = data.success ? [...data.data] : [];

        // Extract gene names already in results to avoid duplicates
        const existingGeneNames = results
          .filter(item => item.entityType === 'gene')
          .map(item => item.text.toUpperCase());

        // If this might be a protein name and we're in gene search, check for protein matches
        if (type === 'gene' && mightBeProteinName(term)) {
          try {
            const proteinResponse = await fetch(`/api/protein/lookup?name=${encodeURIComponent(term)}`);
            const proteinData = await proteinResponse.json();

            if (proteinData.success && proteinData.data.length > 0) {
              // Filter out genes that already exist in autocomplete results (case insensitive)
              const uniqueProteinGenes = proteinData.data.filter(
                geneName => !existingGeneNames.includes(geneName.toUpperCase())
              );

              // Create gene suggestions from unique protein matches
              const proteinGenesSuggestions = uniqueProteinGenes.map(geneName => ({
                text: geneName,
                type: 'Alias',
                entityType: 'gene',
              }));

              // Add unique gene suggestions from protein search at the top
              if (proteinGenesSuggestions.length > 0) {
                results = [...proteinGenesSuggestions, ...results];
              }

              // Update protein lookup status for the UI feedback
              setProteinLookupStatus({
                success: true,
                message: `Found ${proteinData.data.length} genes related to protein "${term}"${proteinData.data.length !== uniqueProteinGenes.length
                    ? ` (${proteinData.data.length - uniqueProteinGenes.length} already in results)`
                    : ''
                  }`
              });
            }
          } catch (error) {
            console.error('Protein lookup in autocomplete failed:', error);
          }
        }

        setAutocompleteResults(results);
        setShowAutocomplete(results.length > 0);
      } catch (error) {
        console.error('Autocomplete fetch failed:', error);
      }
    }, 300),
    [type]
  );
  
  // Memoize the debounced full search function with pagination
  const handleFullSearch = useCallback(
    debounce(async (term, page = 1, pageSize = 10) => {
      if (!term) {
        setSearchResults([]);
        setPagination({
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 0
        });
        return;
      }
      setIsLoading(true);
      try {
        const response = await fetch(
          `${apiEndpoint}?q=${encodeURIComponent(term)}&page=${page}&pageSize=${pageSize}`
        );
        const data = await response.json();
        if (data.success) {
          // Process results to include total_genes_count from count.genes
          const processedResults = data.data.map(paper => ({
            ...paper,
            total_genes_count: data.count?.genes || paper.genes_pagination?.total || 0
          }));
          setSearchResults(processedResults);
          
          // Store pagination info
          if (data.pagination) {
            setPagination(data.pagination);
          }
        }
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setIsLoading(false);
        setShowAutocomplete(false);
      }
    }, 500),
    [apiEndpoint]
  );

  // Function to change page for main paper results
  const handlePageChange = (newPage) => {
    handleFullSearch(searchTerm, newPage, pagination.pageSize);
  };

  // Function to change page size for main paper results
  const handlePageSizeChange = (newPageSize) => {
    handleFullSearch(searchTerm, 1, newPageSize);
  };

  // Function to fetch genes for a specific paper with backend filtering
  const fetchPaperGenesWithFilters = async (pmid, filters = {}) => {
    if (!searchTerm) return;
    
    setIsLoading(true);
    try {
      // Build query parameters with all filters
      const params = new URLSearchParams({
        q: searchTerm,
        pmid: pmid,
        genesPage: filters.page || 1,
        genesPageSize: filters.pageSize || 20,
      });
      
      // Add optional filters if provided
      if (filters.geneFilter) params.append('geneFilter', filters.geneFilter);
      if (filters.geneLocalization && filters.geneLocalization !== 'All') {
        params.append('geneLocalization', filters.geneLocalization);
      }
      if (filters.geneDisplayMode && filters.geneDisplayMode !== 'all') {
        params.append('geneDisplayMode', filters.geneDisplayMode);
      }
      
      const response = await fetch(`${apiEndpoint}?${params.toString()}`);
      const data = await response.json();
      
      if (data.success) {
        // Find the paper and update its data
        const updatedResults = searchResults.map(paper => {
          if (paper.PMID === pmid) {
            const foundPaper = data.data.find(p => p.PMID === pmid);
            if (foundPaper) {
              return {
                ...paper,
                associated_genes: foundPaper.associated_genes || [],
                genes_pagination: foundPaper.genes_pagination || paper.genes_pagination,
                consensus_stats: foundPaper.consensus_stats || paper.consensus_stats,
                available_localizations: foundPaper.available_localizations || paper.available_localizations
              };
            }
          }
          return paper;
        });
        
        setSearchResults(updatedResults);
      }
    } catch (error) {
      console.error('Fetch publication genes with filters failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch mutations for a specific paper with pagination
  const fetchPaperMutations = async (pmid, page = 1, pageSize = 20) => {
    if (!searchTerm) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(
        `${apiEndpoint}?q=${encodeURIComponent(searchTerm)}&pmid=${pmid}&mutationsPage=${page}&mutationsPageSize=${pageSize}`
      );
      const data = await response.json();
      
      if (data.success) {
        // Find the paper and update its mutations
        const updatedResults = searchResults.map(paper => {
          if (paper.PMID === pmid) {
            // Update the paper with new mutations data
            const updatedPaper = {
              ...paper,
              associated_mutations: data.data.find(p => p.PMID === pmid)?.associated_mutations || [],
              mutations_pagination: data.data.find(p => p.PMID === pmid)?.mutations_pagination || paper.mutations_pagination
            };
            
            return updatedPaper;
          }
          return paper;
        });
        
        setSearchResults(updatedResults);
      }
    } catch (error) {
      console.error('Fetch publication mutations failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to detect if the search term might be a protein name
  const mightBeProteinName = (term) => {
    // Simple heuristic - protein names typically have letters and possibly hyphens
    // but aren't just numbers and are at least 3 characters
    return /^[A-Za-z0-9\-]+$/.test(term) &&
      !term.match(/^[0-9]+$/) && // Not just a number
      term.length >= 3;
  };

  // Function to lookup a protein name
  const lookupProteinName = async (name) => {
    try {
      setProteinLookupStatus({ loading: true, message: `Looking up protein "${name}"...` });
      const response = await fetch(`/api/protein/lookup?name=${encodeURIComponent(name)}`);
      const data = await response.json();

      if (data.success && data.data.length > 0) {
        setProteinLookupStatus({
          success: true,
          message: `Found ${data.data.length} genes related to protein "${name}": ${data.data.join(', ')}`
        });
        return data.data;
      } else {
        setProteinLookupStatus({
          info: true,
          message: `No genes found for protein "${name}". Trying regular search.`
        });
        return [];
      }
    } catch (error) {
      console.error('Protein lookup failed:', error);
      setProteinLookupStatus({
        error: true,
        message: `Error looking up protein "${name}": ${error.message}`
      });
      return [];
    }
  };

  // Enhanced search function that handles protein names
  const handleEnhancedSearch = useCallback(async (term, page = 1, pageSize = 10) => {
    if (!term) {
      setSearchResults([]);
      setPagination({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0
      });
      return;
    }

    setIsLoading(true);
    setProteinLookupStatus(null);

    try {
      // Only attempt protein lookup if we're in gene search mode
      if (type === 'gene' && mightBeProteinName(term)) {
        const geneSymbols = await lookupProteinName(term);

        if (geneSymbols.length > 0) {
          // We found gene symbols for this protein
          let allResults = [];

          // Search for each gene symbol
          for (const symbol of geneSymbols) {
            const response = await fetch(`${apiEndpoint}?q=${encodeURIComponent(symbol)}&page=${page}&pageSize=${pageSize}`);
            const data = await response.json();
            if (data.success && data.data.length > 0) {
              allResults = [...allResults, ...data.data];
            }
          }

          // Update search results with all found genes
          setSearchResults(allResults);
          setIsLoading(false);
          setShowAutocomplete(false);
          return;
        }
      }

      // If not a protein or no genes found, do regular search
      await handleFullSearch(term, page, pageSize);

    } catch (error) {
      console.error('Enhanced search failed:', error);
      setProteinLookupStatus({
        error: true,
        message: `Search error: ${error.message}`
      });
      setIsLoading(false);
      setShowAutocomplete(false);
    }
  }, [apiEndpoint, type, handleFullSearch]);

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    fetchAutocompleteSuggestions(term);
  };

  const handleAutocompleteClick = (suggestion) => {
    setSearchTerm(suggestion.text);
    // Use enhanced search instead of regular search
    if (type === 'gene') {
      handleEnhancedSearch(suggestion.text);
    } else {
      handleFullSearch(suggestion.text);
    }
    setShowAutocomplete(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Use enhanced search for gene searches, regular search for others
    if (type === 'gene') {
      handleEnhancedSearch(searchTerm);
    } else {
      handleFullSearch(searchTerm);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setAutocompleteResults([]);
    setShowAutocomplete(false);
    setProteinLookupStatus(null);
    setPagination({
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 0
    });
    // Clear all tab data
    setTabData({});
    setTabLoading({});
    setTabErrors({});
  };

  // Add a generic function to fetch tab data with loading state management
  const fetchTabData = async (entityId, tabType, apiUrl, params = {}) => {
    const key = `${entityId}-${tabType}`;
    
    // Set loading state
    setTabLoading(prev => ({ ...prev, [key]: true }));
    setTabErrors(prev => ({ ...prev, [key]: null }));
    
    try {
      // Build query parameters
      const queryParams = new URLSearchParams(params);
      
      // Fetch data
      const response = await fetch(`${apiUrl}?${queryParams.toString()}`);
      const data = await response.json();
      
      if (data.success) {
        // Store data in tabData state
        setTabData(prev => ({ ...prev, [key]: data.data }));
        return data;
      } else {
        // Handle API error
        setTabErrors(prev => ({ ...prev, [key]: data.message || `Failed to load ${tabType} data` }));
        return null;
      }
    } catch (error) {
      console.error(`Error fetching ${tabType} for ${entityId}:`, error);
      setTabErrors(prev => ({ ...prev, [key]: `Error: ${error.message}` }));
      return null;
    } finally {
      setTabLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Clear tab data for a specific entity or tab
  const clearTabData = (entityId, tabType = null) => {
    if (tabType) {
      // Clear specific tab data
      const key = `${entityId}-${tabType}`;
      setTabData(prev => {
        const newData = { ...prev };
        delete newData[key];
        return newData;
      });
      setTabLoading(prev => {
        const newLoading = { ...prev };
        delete newLoading[key];
        return newLoading;
      });
      setTabErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    } else {
      // Clear all tabs for this entity
      setTabData(prev => {
        const newData = { ...prev };
        Object.keys(newData).forEach(key => {
          if (key.startsWith(`${entityId}-`)) {
            delete newData[key];
          }
        });
        return newData;
      });
      setTabLoading(prev => {
        const newLoading = { ...prev };
        Object.keys(newLoading).forEach(key => {
          if (key.startsWith(`${entityId}-`)) {
            delete newLoading[key];
          }
        });
        return newLoading;
      });
      setTabErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith(`${entityId}-`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  };

  // Function to get tab data
  const getTabData = (entityId, tabType) => {
    const key = `${entityId}-${tabType}`;
    return tabData[key] || [];
  };

  // Function to check if a tab is loading
  const isTabLoading = (entityId, tabType) => {
    const key = `${entityId}-${tabType}`;
    return !!tabLoading[key];
  };

  // Function to get tab error
  const getTabError = (entityId, tabType) => {
    const key = `${entityId}-${tabType}`;
    return tabErrors[key] || null;
  };

  return {
    searchResults,
    setSearchResults,
    autocompleteResults,
    isLoading,
    setIsLoading,
    searchTerm,
    showAutocomplete,
    autocompleteRef,
    inputRef,
    handleInputChange,
    handleAutocompleteClick,
    handleSearchSubmit,
    clearSearch,
    setShowAutocomplete,
    handleFullSearch,
    handleEnhancedSearch,
    setSearchTerm,
    proteinLookupStatus,
    // Pagination related
    pagination,
    handlePageChange,
    handlePageSizeChange,
    // Backend filtering for genes
    fetchPaperGenesWithFilters,
    fetchPaperMutations,
    // New tab data management
    tabData,
    tabLoading,
    tabErrors,
    setTabData,
    setTabLoading,
    setTabErrors,
    fetchTabData,
    clearTabData,
    getTabData,
    isTabLoading,
    getTabError,
  };
};